import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Autocomplete,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import {
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as CheckIcon,
  AutoFixHigh as AiIcon,
} from '@mui/icons-material'
import { ComplianceRule, ComplianceRuleInput } from '../../types/compliance'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

interface ComplianceRuleFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: ComplianceRuleInput) => Promise<void>
  rule?: ComplianceRule | null
  existingCategories: string[]
  onCategoryCreated?: (category: string) => void
  onPdfSuccess?: () => void
  initialCategory?: string
}

interface PDFMetadata {
  pages: number
  title?: string
  subject?: string
  author?: string
}

interface UploadResult {
  success: boolean
  rule?: any
  pdfMetadata?: PDFMetadata
  error?: string
}

export const ComplianceRuleForm: React.FC<ComplianceRuleFormProps> = ({
  open,
  onClose,
  onSubmit,
  rule,
  existingCategories,
  onCategoryCreated,
  onPdfSuccess,
  initialCategory,
}) => {
  const { t } = useTranslation('compliance')

  // タブ状態（編集モードでは手動入力タブのみ）
  const [activeTab, setActiveTab] = useState(0)

  // 手動入力フォーム用
  const [formData, setFormData] = useState<ComplianceRuleInput>({
    title: '',
    description: '',
    content: '',
    category: '',
  })
  const [loading, setLoading] = useState(false)
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryDescription, setNewCategoryDescription] = useState('')

  // PDFアップロード用
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [pdfFormData, setPdfFormData] = useState({
    title: '',
    description: '',
    category: '',
  })
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [pdfError, setPdfError] = useState('')
  const [pdfSuccess, setPdfSuccess] = useState('')
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const [useAiCorrection, setUseAiCorrection] = useState(false)

  const isEdit = !!rule

  // フォーム初期化
  useEffect(() => {
    if (rule) {
      setFormData({
        title: rule.title,
        description: rule.description,
        content: rule.content,
        category: rule.category,
      })
      // 編集モードでは手動入力タブに固定
      setActiveTab(0)
    } else {
      // 新規作成時：initialCategoryがあればそれを使用
      setFormData({
        title: '',
        description: '',
        content: '',
        category: initialCategory || '',
      })
      // PDFフォームにも初期カテゴリを設定
      setPdfFormData((prev) => ({
        ...prev,
        category: initialCategory || '',
      }))
    }
    // ダイアログが閉じられた時にPDF関連の状態をリセット
    if (!open) {
      setActiveTab(0)
      setSelectedFile(null)
      setPdfFormData({ title: '', description: '', category: '' })
      setPdfError('')
      setPdfSuccess('')
      setUploadResult(null)
      setUploadProgress(0)
      setIsDragActive(false)
      setUseAiCorrection(false)
    }
  }, [rule, open, initialCategory])

  const handleChange =
    (field: keyof ComplianceRuleInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }))
    }

  const handleCategoryChange = (event: any, newValue: string | null) => {
    try {
      setFormData((prev) => ({
        ...prev,
        category: newValue || '',
      }))
    } catch (error) {
      console.error('Category change error:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title || !formData.content || !formData.category) {
      return
    }

    try {
      setLoading(true)
      await onSubmit(formData)
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading && !uploading) {
      onClose()
    }
  }

  // === PDF アップロード関連 ===
  const processFile = useCallback(
    (file: File) => {
      // ファイルタイプ確認
      if (file.type !== 'application/pdf') {
        setPdfError(t('pdf.errors.invalidFormat'))
        return
      }

      // ファイルサイズ確認 (20MB)
      if (file.size > 20 * 1024 * 1024) {
        setPdfError(t('pdf.errors.tooLarge'))
        return
      }

      setSelectedFile(file)
      setPdfError('')

      // ファイル名から推定タイトルを設定（既に入力されていない場合）
      if (!pdfFormData.title) {
        const nameWithoutExt = file.name.replace(/\.pdf$/i, '').replace(/[_-]/g, ' ')
        setPdfFormData((prev) => ({ ...prev, title: nameWithoutExt }))
      }
    },
    [pdfFormData.title, t]
  )

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        processFile(file)
      }
    },
    [processFile]
  )

  // ドラッグ&ドロップイベントハンドラー
  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (!isDragActive && !uploading && !selectedFile) {
        setIsDragActive(true)
      }
    },
    [isDragActive, uploading, selectedFile]
  )

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (!uploading && !selectedFile) {
        setIsDragActive(true)
      }
    },
    [uploading, selectedFile]
  )

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const x = event.clientX
    const y = event.clientY

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragActive(false)
    }
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      setIsDragActive(false)

      if (uploading || selectedFile) return

      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        processFile(file)
      }
    },
    [uploading, selectedFile, processFile]
  )

  const handlePdfFormDataChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement> | any, newValue?: string | null) => {
      const value = newValue !== undefined ? newValue : event.target.value
      setPdfFormData((prev) => ({
        ...prev,
        [field]: value || '',
      }))
    }

  const handleUpload = async () => {
    if (!selectedFile) {
      setPdfError(t('pdf.errors.noFile'))
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)
      setPdfError('')
      setPdfSuccess('')

      // FormData作成
      const uploadFormData = new FormData()
      uploadFormData.append('pdfFile', selectedFile)

      // オプションフィールド（空でない場合のみ追加）
      if (pdfFormData.title.trim()) {
        uploadFormData.append('title', pdfFormData.title.trim())
      }
      if (pdfFormData.description.trim()) {
        uploadFormData.append('description', pdfFormData.description.trim())
      }
      if (pdfFormData.category.trim()) {
        uploadFormData.append('category', pdfFormData.category.trim())
      }
      // AI補正オプション
      if (useAiCorrection) {
        uploadFormData.append('useAiCorrection', 'true')
      }

      // 進捗シミュレーション
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval)
            return prev
          }
          return prev + 10
        })
      }, 200)

      const token = localStorage.getItem('authToken')
      const response = await fetch(`${API_BASE_URL}/compliance/pdf`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadFormData,
      })

      clearInterval(progressInterval)
      setUploadProgress(100)

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || t('pdf.errors.parseFailed'))
      }

      setPdfSuccess(result.message)
      setUploadResult({
        success: true,
        rule: result.rule,
        pdfMetadata: result.rule.pdfMetadata,
      })

      // 2秒後にモーダルを閉じて成功コールバック実行
      setTimeout(() => {
        if (onPdfSuccess) {
          onPdfSuccess()
        }
        onClose()
      }, 2000)
    } catch (err) {
      console.error('PDF アップロードエラー:', err)
      setPdfError(err instanceof Error ? err.message : t('pdf.errors.parseFailed'))
    } finally {
      setUploading(false)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleOpenCategoryDialog = () => {
    setNewCategoryName('')
    setNewCategoryDescription('')
    setCategoryDialogOpen(true)
  }

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false)
    setNewCategoryName('')
    setNewCategoryDescription('')
  }

  const handleCreateCategory = () => {
    if (!newCategoryName.trim()) return

    const trimmedName = newCategoryName.trim()

    // 新規カテゴリを作成
    if (!existingCategories.includes(trimmedName) && onCategoryCreated) {
      onCategoryCreated(trimmedName)
    }

    // アクティブなタブに基づいてカテゴリを設定
    if (activeTab === 0) {
      setFormData((prev) => ({ ...prev, category: trimmedName }))
    } else {
      setPdfFormData((prev) => ({ ...prev, category: trimmedName }))
    }
    handleCloseCategoryDialog()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={
        activeTab === 0
          ? {
              component: 'form',
              onSubmit: handleSubmit,
            }
          : undefined
      }
    >
      <DialogTitle>{isEdit ? t('edit.title') : t('create.title')}</DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* 編集モードではタブを表示しない */}
        {!isEdit && (
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label={t('create.manual')} disabled={uploading} />
            <Tab label={t('create.fromPdf')} disabled={loading} />
          </Tabs>
        )}

        {/* タブ0: 手動入力 */}
        {activeTab === 0 && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label={t('form.title')}
              value={formData.title}
              onChange={handleChange('title')}
              fullWidth
              required
              disabled={loading}
            />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Autocomplete
                freeSolo
                options={existingCategories}
                value={formData.category}
                onChange={handleCategoryChange}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label={t('form.category')} required disabled={loading} />
                )}
              />
              <Tooltip title={t('categories.createNew')}>
                <IconButton
                  onClick={handleOpenCategoryDialog}
                  disabled={loading}
                  sx={{ mt: 1 }}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <TextField
              label={t('form.description')}
              value={formData.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={3}
              disabled={loading}
            />

            <TextField
              label={t('form.content')}
              value={formData.content}
              onChange={handleChange('content')}
              fullWidth
              required
              multiline
              rows={8}
              disabled={loading}
              helperText={t('form.contentHelp')}
            />
          </Box>
        )}

        {/* タブ1: PDFからインポート */}
        {activeTab === 1 && !isEdit && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* PDFファイルアップロード */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                {t('pdf.selectFile')}
              </Typography>

              <Paper
                sx={{
                  p: 3,
                  border: isDragActive ? '2px dashed #1F3A5F' : '2px dashed #E5E7EB',
                  textAlign: 'center',
                  cursor: selectedFile ? 'default' : 'pointer',
                  backgroundColor: selectedFile
                    ? 'action.hover'
                    : isDragActive
                      ? 'primary.50'
                      : 'background.paper',
                  '&:hover': {
                    backgroundColor: selectedFile
                      ? 'action.hover'
                      : isDragActive
                        ? 'primary.100'
                        : 'action.hover',
                  },
                  transition: 'all 0.2s ease-in-out',
                  transform: isDragActive ? 'scale(1.02)' : 'scale(1)',
                }}
                onClick={() => {
                  if (!selectedFile && !uploading) {
                    document.getElementById('pdf-file-input')?.click()
                  }
                }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  id="pdf-file-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />

                {selectedFile ? (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}
                  >
                    <PdfIcon color="error" sx={{ fontSize: 48 }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(selectedFile.size)}
                      </Typography>
                      <Chip label="PDF" color="error" size="small" />
                    </Box>
                    {!uploading && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedFile(null)
                          setPdfFormData({ title: '', description: '', category: '' })
                        }}
                      >
                        {t('pdf.change')}
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon
                      sx={{
                        fontSize: 48,
                        color: isDragActive ? 'primary.main' : 'text.secondary',
                        mb: 1,
                        transition: 'color 0.2s ease-in-out',
                      }}
                    />
                    <Typography
                      variant="h6"
                      color={isDragActive ? 'primary.main' : 'text.secondary'}
                      sx={{ transition: 'color 0.2s ease-in-out' }}
                    >
                      {isDragActive ? t('pdf.dropzoneActive') : t('pdf.dropzone')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isDragActive ? '' : t('pdf.clickToSelect')}
                    </Typography>
                    {!isDragActive && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        {t('pdf.supportedFormat')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>

            {/* アップロード進捗 */}
            {uploading && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {t('pdf.processing')} ({uploadProgress}%)
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            {/* エラー表示 */}
            {pdfError && <Alert severity="error">{pdfError}</Alert>}

            {/* 成功表示 */}
            {pdfSuccess && uploadResult && (
              <Alert severity="success" icon={<CheckIcon />}>
                <Typography variant="body1" gutterBottom>
                  {t('pdf.success')}
                </Typography>
                {uploadResult.pdfMetadata && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {t('pdf.pageCount', { count: uploadResult.pdfMetadata.pages })}
                    </Typography>
                    {uploadResult.rule && (
                      <Typography variant="body2" color="text.secondary">
                        {t('pdf.ruleTitle', { title: uploadResult.rule.title })}
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
            )}

            {/* 規範情報入力フィールド */}
            {!pdfSuccess && (
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                  {t('pdf.ruleInfo')}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('form.title')}
                    value={pdfFormData.title}
                    onChange={handlePdfFormDataChange('title')}
                    fullWidth
                    required
                    disabled={uploading}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Autocomplete
                      freeSolo
                      options={existingCategories}
                      value={pdfFormData.category}
                      onChange={handlePdfFormDataChange('category')}
                      disabled={uploading}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...params} label={t('form.category')} required />
                      )}
                    />
                    <Tooltip title={t('categories.createNew')}>
                      <IconButton
                        onClick={handleOpenCategoryDialog}
                        disabled={uploading}
                        sx={{ mt: 1 }}
                        color="primary"
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <TextField
                    label={t('form.description')}
                    value={pdfFormData.description}
                    onChange={handlePdfFormDataChange('description')}
                    fullWidth
                    multiline
                    rows={2}
                    disabled={uploading}
                  />

                  {/* AI補正オプション */}
                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useAiCorrection}
                          onChange={(e) => setUseAiCorrection(e.target.checked)}
                          disabled={uploading}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AiIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {t('pdf.useAiCorrection', 'AI補正を使用する（OCR精度向上）')}
                          </Typography>
                        </Box>
                      }
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 4, display: 'block' }}
                    >
                      {t(
                        'pdf.aiCorrectionHelp',
                        'スキャンされたPDFの場合、OpenAI APIを使用してOCRの誤認識を自動補正します。処理時間が長くなる場合があります。'
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading || uploading}>
          {pdfSuccess ? t('pdf.close') : t('form.cancel')}
        </Button>

        {/* 手動入力タブの送信ボタン */}
        {activeTab === 0 && (
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.title || !formData.content || !formData.category}
          >
            {loading ? t('form.saving') : isEdit ? t('form.update') : t('categories.create')}
          </Button>
        )}

        {/* PDFタブのアップロードボタン */}
        {activeTab === 1 && !pdfSuccess && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={
              !selectedFile ||
              uploading ||
              !pdfFormData.title.trim() ||
              !pdfFormData.category.trim()
            }
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? t('pdf.uploadProgress') : t('pdf.createFromPdf')}
          </Button>
        )}
      </DialogActions>

      {/* カテゴリ新規作成ダイアログ */}
      <Dialog open={categoryDialogOpen} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('categories.createNew')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('categories.name')}
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              fullWidth
              required
              autoFocus
              placeholder={t('categories.namePlaceholder')}
            />
            <TextField
              label={t('form.description')}
              value={newCategoryDescription}
              onChange={(e) => setNewCategoryDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder={t('categories.descPlaceholder')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryDialog}>{t('categories.cancel')}</Button>
          <Button
            onClick={handleCreateCategory}
            variant="contained"
            disabled={!newCategoryName.trim()}
          >
            {t('categories.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
